import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import {
  savePrintSettings,
  usePrintSettings,
  useStoreSettings
} from "../../controllers/settings.controller";
import { toast } from "react-hot-toast";
import { mutate } from "swr";
import { fetchPrintersFromWebSocketAgent, getCachedPrinters, saveCachedPrinters, getNetworkPrinters } from "../../helpers/PrinterHelper";
import { BILL_FORMATS, generateRoomBillHTML, getNepaliDateBS, formatReceiptDateTime } from "../../helpers/BillFormatsHelper";
import { 
  IconCheck, 
  IconEye, 
  IconPrinter, 
  IconReceipt, 
  IconFileText, 
  IconStar, 
  IconX,
  IconShieldCheck,
  IconChevronLeft,
  IconChevronRight,
  IconDeviceFloppy
} from "@tabler/icons-react";

export default function PrintSettingsPage() {
  const { t } = useTranslation();
  const enablePrintRef = useRef();
  const showStoreDetailsRef = useRef();
  const showCustomerDetailsRef = useRef();
  const pageSizeRef = useRef();
  const headerRef = useRef();
  const footerRef = useRef();
  const showNotesRef = useRef();
  const printTokenRef = useRef();
  const printCountRef = useRef();

  const { APIURL, data, error, isLoading } = usePrintSettings();
  const { data: storeSettings } = useStoreSettings();

  const [kdsAutoPrint, setKdsAutoPrint] = useState(localStorage.getItem('kdsAutoPrint') !== 'false');
  const [printers, setPrinters] = useState(getCachedPrinters());
  const [networkPrinters, setNetworkPrinters] = useState(getNetworkPrinters());
  const [billPrinter, setBillPrinter] = useState(localStorage.getItem('billPrinter') || '');

  // Active bill format selection
  const [selectedBillFormat, setSelectedBillFormat] = useState(
    data?.billFormat || localStorage.getItem('bill_format') || 'retail_tax_invoice'
  );

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState('food'); // 'food' | 'room'
  const [previewFormat, setPreviewFormat] = useState('retail_tax_invoice');

  useEffect(() => {
    if (data?.billFormat) {
      setSelectedBillFormat(data.billFormat);
      localStorage.setItem('bill_format', data.billFormat);
    }
  }, [data]);

  useEffect(() => {
    try {
      const cached = getCachedPrinters();
      if (cached && cached.length > 0) {
        setPrinters(cached);
      }
      setNetworkPrinters(getNetworkPrinters());

      const isElectron = window && window.process && window.process.type;
      if (isElectron && window.require) {
        const { ipcRenderer } = window.require("electron");
        ipcRenderer.invoke('get-printers').then((p) => {
          if (Array.isArray(p) && p.length > 0) {
            setPrinters(p);
            saveCachedPrinters(p);
          }
        }).catch((err) => {
          console.error('[PrintSettings] Failed to get printers:', err);
        });
      } else {
        fetchPrintersFromWebSocketAgent().then(list => {
          if (list && list.length > 0) {
            setPrinters(list);
            saveCachedPrinters(list);
          }
        }).catch((err) => {
          console.log('[PrintSettings] Print agent unreachable or offline:', err?.message);
        });
      }
    } catch (err) {
      console.error('[PrintSettings] Error loading printers:', err);
    }
  }, []);

  const {
    pageFormat = "80",
    header = "",
    footer = "",
    showNotes = true,
    isEnablePrint = true,
    showStoreDetails = true,
    showCustomerDetails = true,
    printToken = true,
    printCount = 1,
  } = data || {};

  const btnSave = async () => {
    const enablePrint = enablePrintRef.current?.checked ?? true;
    const showStoreDetails = showStoreDetailsRef.current?.checked ?? true;
    const showCustomerDetails = showCustomerDetailsRef.current?.checked ?? true;
    const pageSize = pageSizeRef.current?.value || "80";
    const headerVal = headerRef.current?.value || "";
    const footerVal = footerRef.current?.value || "";
    const showNotes = showNotesRef.current?.checked ?? true;
    const printToken = printTokenRef.current?.checked ?? true;
    const printCountVal = parseInt(printCountRef.current?.value) || 1;

    try {
      localStorage.setItem('kdsAutoPrint', kdsAutoPrint.toString());
      localStorage.setItem('billPrinter', billPrinter);
      localStorage.setItem('bill_format', selectedBillFormat);
      localStorage.setItem('page_format', pageSize);

      toast.loading(t('print_settings.please_wait'));
      const res = await savePrintSettings(
        pageSize,
        headerVal,
        footerVal,
        showNotes,
        enablePrint,
        showStoreDetails,
        showCustomerDetails,
        printToken,
        printCountVal,
        selectedBillFormat
      );

      if (res.status === 200) {
        await mutate(APIURL);
        toast.dismiss();
        toast.success(res.data.message || "Print settings saved successfully!");
      }
    } catch (err) {
      const message = err?.response?.data?.message || t('print_settings.something_went_wrong');
      console.error(err);
      toast.dismiss();
      toast.error(message);
    }
  };

  const btnQuickSaveFormat = async (formatId) => {
    setSelectedBillFormat(formatId);
    setPreviewFormat(formatId);
    localStorage.setItem('bill_format', formatId);

    const pageSize = pageSizeRef.current?.value || pageFormat || "80";
    localStorage.setItem('page_format', pageSize);
    const headerVal = headerRef.current?.value || header || "";
    const footerVal = footerRef.current?.value || footer || "";
    const showNotesVal = showNotesRef.current?.checked ?? showNotes ?? true;
    const enablePrintVal = enablePrintRef.current?.checked ?? isEnablePrint ?? true;
    const showStoreDetailsVal = showStoreDetailsRef.current?.checked ?? showStoreDetails ?? true;
    const showCustomerDetailsVal = showCustomerDetailsRef.current?.checked ?? showCustomerDetails ?? true;
    const printTokenVal = printTokenRef.current?.checked ?? printToken ?? true;
    const printCountVal = parseInt(printCountRef.current?.value) || printCount || 1;

    try {
      localStorage.setItem('kdsAutoPrint', kdsAutoPrint.toString());
      localStorage.setItem('billPrinter', billPrinter);

      const res = await savePrintSettings(
        pageSize,
        headerVal,
        footerVal,
        showNotesVal,
        enablePrintVal,
        showStoreDetailsVal,
        showCustomerDetailsVal,
        printTokenVal,
        printCountVal,
        formatId
      );
      if (res.status === 200) {
        await mutate(APIURL);
        const formatName = BILL_FORMATS.find(f => f.id === formatId)?.shortName || formatId;
        toast.success(`Format changed & saved: "${formatName}"!`);
      }
    } catch (err) {
      console.warn('[PrintSettings] Quick-save format note:', err?.message);
      const formatName = BILL_FORMATS.find(f => f.id === formatId)?.shortName || formatId;
      toast.success(`Selected "${formatName}". Click Save Settings to persist.`);
    }
  };

  const handleNextFormat = () => {
    const currentIndex = BILL_FORMATS.findIndex(f => f.id === selectedBillFormat);
    const nextIndex = (currentIndex + 1) % BILL_FORMATS.length;
    btnQuickSaveFormat(BILL_FORMATS[nextIndex].id);
  };

  const handlePrevFormat = () => {
    const currentIndex = BILL_FORMATS.findIndex(f => f.id === selectedBillFormat);
    const prevIndex = (currentIndex - 1 + BILL_FORMATS.length) % BILL_FORMATS.length;
    btnQuickSaveFormat(BILL_FORMATS[prevIndex].id);
  };

  // Keyboard arrow listener for format selection
  useEffect(() => {
    if (isLoading || error) return;

    const handleKeyDown = (e) => {
      // Don't intercept if user is typing in an input or textarea
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (previewOpen) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleNextFormat();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrevFormat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBillFormat, previewOpen, pageFormat, header, footer, showNotes, isEnablePrint, showStoreDetails, showCustomerDetails, printToken, printCount, kdsAutoPrint, billPrinter, isLoading, error]);

  // Trigger live preview
  const handleOpenPreview = (type, formatId) => {
    setPreviewType(type);
    setPreviewFormat(formatId);
    setPreviewOpen(true);
  };

  // Mock data for room folio preview
  const mockFolioData = {
    booking: {
      id: 1042,
      guest_name: "Aarav Sharma",
      guest_phone: "9841234567",
      guest_pan: "300481234",
      room_number: "204",
      room_type_name: "Deluxe Suite",
      check_in: new Date(Date.now() - 2 * 86400000).toISOString(),
      check_out: new Date().toISOString(),
      room_rate: 3500.00,
      invoice_no: "INV-R1042"
    },
    nights: 2,
    roomTotal: 7000.00,
    extraCharges: [
      { id: 1, title: "Airport Pickup Service", amount: 1500.00 },
      { id: 2, title: "Laundry & Pressing", amount: 450.00 }
    ],
    extraTotal: 1950.00,
    orders: [
      {
        id: 582,
        order_total: 1250.00,
        items: [
          { item_name: "Chicken Biryani", quantity: 2 },
          { item_name: "Fresh Lime Soda", quantity: 2 }
        ]
      }
    ],
    orderTotal: 1250.00,
    totalCharges: 10200.00,
    payments: 5000.00,
    balance: 5200.00,
    store: storeSettings || {
      store_name: "Hotel RestroPro & Dining",
      address: "Lakeside-6, Pokhara, Nepal",
      phone: "+977 61-460000",
      email: "info@ehospitalitynepal.com",
      pan_no: "601234567",
      is_vat_registered: 1,
      currency: "Rs."
    }
  };

  const getFormatIcon = (iconName) => {
    switch (iconName) {
      case 'printer': return <IconPrinter size={20} />;
      case 'receipt': return <IconReceipt size={20} />;
      case 'file-text': return <IconFileText size={20} />;
      case 'star': return <IconStar size={20} />;
      default: return <IconFileText size={20} />;
    }
  };

  if (isLoading) {
    return <Page className="px-8 py-6">{t('print_settings.please_wait')}</Page>;
  }

  if (error) {
    console.error(error);
    return <Page className="px-8 py-6">{t('print_settings.error_loading_data')}</Page>;
  }

  return (
    <Page className="px-8 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
        <div>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconPrinter className="text-restro-green" size={32} /> {t('print_settings.title')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure printing parameters, printer devices, and select your IRD-compliant bill template for Food and Room bills.
          </p>
        </div>
        <button
          onClick={btnSave}
          className="btn bg-restro-green hover:bg-emerald-700 text-white px-6 shadow-md rounded-xl"
        >
          <IconCheck size={18} /> {t('print_settings.save')}
        </button>
      </div>

      <div className="mt-8 space-y-10">
        {/* ========================================================================= */}
        {/* SECTION 1: BILL / INVOICE FORMAT SELECTION (IRD COMPLIANT) */}
        {/* ========================================================================= */}
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                  Bill / Invoice Template Format
                </h4>
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                  <IconShieldCheck size={14} /> IRD Tax Invoice Compliant
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Choose the exact layout format to be printed for <b>Food Bills</b> (Restaurant POS / Orders) and <b>Room Bills</b> (Room Folio / Checkout). Use the <b>arrow buttons</b> or <b>arrow keys [← / →]</b> to navigate formats.
              </p>
            </div>

            {/* Moving arrow navigation controls & quick switch */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <div className="flex items-center bg-gray-100 dark:bg-zinc-800 rounded-xl p-1 border border-gray-200 dark:border-zinc-700">
                <button
                  type="button"
                  onClick={handlePrevFormat}
                  title="Previous Format (Left Arrow Key)"
                  className="btn btn-xs btn-ghost text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-zinc-700 rounded-lg gap-1"
                >
                  <IconChevronLeft size={16} /> Prev
                </button>
                <span className="text-xs font-bold px-2 text-emerald-700 dark:text-emerald-400">
                  {BILL_FORMATS.findIndex(f => f.id === selectedBillFormat) + 1} / {BILL_FORMATS.length}
                </span>
                <button
                  type="button"
                  onClick={handleNextFormat}
                  title="Next Format (Right Arrow Key)"
                  className="btn btn-xs btn-ghost text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-zinc-700 rounded-lg gap-1"
                >
                  Next <IconChevronRight size={16} />
                </button>
              </div>

              {/* Quick Format Dropdown */}
              <select
                className="select select-bordered select-xs rounded-xl font-medium dark:bg-zinc-800 dark:text-white"
                value={selectedBillFormat}
                onChange={(e) => btnQuickSaveFormat(e.target.value)}
              >
                {BILL_FORMATS.map((f, i) => (
                  <option key={f.id} value={f.id}>
                    {i + 1}. {f.shortName || f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" role="radiogroup" aria-label="Bill Format Selection">
            {BILL_FORMATS.map((fmt, index) => {
              const isSelected = selectedBillFormat === fmt.id;
              return (
                <div
                  key={fmt.id}
                  tabIndex={0}
                  role="radio"
                  aria-checked={isSelected}
                  data-format-index={index}
                  onClick={() => btnQuickSaveFormat(fmt.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      btnQuickSaveFormat(fmt.id);
                    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                      e.preventDefault();
                      const nextIdx = (index + 1) % BILL_FORMATS.length;
                      const nextCard = document.querySelector(`[data-format-index="${nextIdx}"]`);
                      if (nextCard) {
                        nextCard.focus();
                        btnQuickSaveFormat(BILL_FORMATS[nextIdx].id);
                      }
                    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                      e.preventDefault();
                      const prevIdx = (index - 1 + BILL_FORMATS.length) % BILL_FORMATS.length;
                      const prevCard = document.querySelector(`[data-format-index="${prevIdx}"]`);
                      if (prevCard) {
                        prevCard.focus();
                        btnQuickSaveFormat(BILL_FORMATS[prevIdx].id);
                      }
                    }
                  }}
                  className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-colors duration-150 flex flex-col justify-between outline-none focus:ring-2 focus:ring-emerald-500 ${
                    isSelected
                      ? "border-emerald-600 bg-emerald-50/30 dark:bg-emerald-950/20 shadow-md"
                      : "border-gray-200 dark:border-zinc-800 hover:border-gray-300 bg-white dark:bg-zinc-900"
                  }`}
                >
                  <div>
                    {/* Header line */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`p-2.5 rounded-xl ${isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300'}`}>
                          {getFormatIcon(fmt.icon)}
                        </div>
                        <div>
                          <h5 className="font-bold text-base text-gray-900 dark:text-white">{fmt.name}</h5>
                          <span className="text-xs text-gray-500 font-medium">{fmt.paper}</span>
                        </div>
                      </div>

                      {/* Radio Indicator */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                        isSelected ? "border-emerald-600 bg-emerald-600 text-white" : "border-gray-300 dark:border-zinc-700"
                      }`}>
                        {isSelected && <IconCheck size={14} stroke={3} />}
                      </div>
                    </div>

                    {/* Badge and Description */}
                    <div className="mt-3">
                      <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-md border ${fmt.badgeColor} mb-2`}>
                        {fmt.badge}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                        {fmt.description}
                      </p>
                    </div>

                    {/* Feature Highlights */}
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-zinc-800 grid grid-cols-2 gap-1 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><IconCheck size={12} className="text-emerald-600" /> Seller & Buyer PAN</span>
                      <span className="flex items-center gap-1"><IconCheck size={12} className="text-emerald-600" /> Taxable & 13% VAT</span>
                      <span className="flex items-center gap-1"><IconCheck size={12} className="text-emerald-600" /> Amount in Words</span>
                      <span className="flex items-center gap-1"><IconCheck size={12} className="text-emerald-600" /> Nepal IRD Standard</span>
                    </div>
                  </div>

                  {/* Actions / Previews */}
                  <div className="mt-5 pt-3 border-t border-gray-100 dark:border-zinc-800 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-gray-400">Live Preview:</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPreview('food', fmt.id);
                        }}
                        className="btn btn-xs btn-outline btn-neutral gap-1 rounded-lg"
                      >
                        <IconEye size={12} /> Food Bill
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPreview('room', fmt.id);
                        }}
                        className="btn btn-xs btn-outline btn-neutral gap-1 rounded-lg"
                      >
                        <IconEye size={12} /> Room Bill
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SECTION 2: PRINTER DEVICES & HARDWARE SETTINGS */}
        {/* ========================================================================= */}
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-3xl p-6 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 dark:text-white border-b pb-4 mb-6">
            Printers & Hardware Configuration
          </h4>

          {/* Kitchen KOT Printer Types & Category Routing Link Banner */}
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <h5 className="font-bold text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
                <IconPrinter size={18} /> Kitchen KOT Printer Types & Category Routing
              </h5>
              <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                Configure station printer types (Kitchen Veg, Kitchen Non-Veg, Bar) and assign menu categories to print separate KOT slips.
              </p>
            </div>
            <a
              href="/dashboard/settings/printers"
              className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm self-start sm:self-center"
            >
              Configure Printer Types &rarr;
            </a>
          </div>

          {/* Default Bill Printer */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-gray-100 dark:border-zinc-800">
            <div>
              <h5 className="font-bold text-gray-900 dark:text-white">Default Receipt/Bill Printer</h5>
              <p className="text-xs text-gray-500 mt-0.5">
                Select target printer device for automatic or silent printing.
              </p>
            </div>
            <div className="mt-3 md:mt-0">
              <select
                className="select select-bordered select-sm w-full md:w-72 dark:bg-black dark:text-white rounded-xl"
                value={billPrinter}
                onChange={(e) => setBillPrinter(e.target.value)}
              >
                <option value="">-- Browser Print Dialog (Standard) --</option>
                {billPrinter && !printers.some(p => p.name === billPrinter) && !networkPrinters.some(np => `tcp://${np.ip}:${np.port || 9100}` === billPrinter || `IP:${np.ip}:${np.port || 9100}` === billPrinter) && (
                  <option value={billPrinter}>{billPrinter} (Configured)</option>
                )}
                {printers.length > 0 && (
                  <optgroup label="Local OS / USB Printers">
                    {printers.map((p, i) => (
                      <option key={`os-${i}`} value={p.name}>{p.displayName || p.name}</option>
                    ))}
                  </optgroup>
                )}
                {networkPrinters.length > 0 && (
                  <optgroup label="WiFi / Network IP Printers">
                    {networkPrinters.map((np, i) => (
                      <option key={`net-${i}`} value={`tcp://${np.ip}:${np.port || 9100}`}>{np.name} ({np.ip})</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>

          {/* Paper roll size */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-gray-100 dark:border-zinc-800">
            <div>
              <h5 className="font-bold text-gray-900 dark:text-white">{t('print_settings.format_page_size')}</h5>
              <p className="text-xs text-gray-500 mt-0.5">
                Paper width for thermal POS receipt printers (80mm standard or 57mm compact roll).
              </p>
            </div>
            <div className="mt-3 md:mt-0">
              <select
                ref={pageSizeRef}
                defaultValue={pageFormat}
                name="pageSize"
                id="pageSize"
                className="select select-bordered select-sm w-full md:w-72 dark:bg-black dark:text-white rounded-xl"
              >
                <option value="80">80mm (Standard POS Roll)</option>
                <option value="70">70mm (Medium POS Roll)</option>
                <option value="57">57mm / 58mm (Compact Roll)</option>
              </select>
            </div>
          </div>

          {/* Number of copies */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-gray-100 dark:border-zinc-800">
            <div>
              <h5 className="font-bold text-gray-900 dark:text-white">Number of Bill Copies</h5>
              <p className="text-xs text-gray-500 mt-0.5">How many copies of the invoice to print automatically per transaction.</p>
            </div>
            <div className="mt-3 md:mt-0">
              <input
                ref={printCountRef}
                defaultValue={printCount || 1}
                type="number"
                min="1"
                max="10"
                name="printCount"
                className="input input-bordered input-sm w-28 rounded-xl font-bold text-center"
              />
            </div>
          </div>

          {/* Switches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            {/* Enable Print */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="enablePrint" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                {t('print_settings.enable_print')}
              </label>
              <input
                ref={enablePrintRef}
                defaultChecked={isEnablePrint}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>

            {/* Show Store Details */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="showStoreDetails" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                {t('print_settings.show_store_details')}
              </label>
              <input
                ref={showStoreDetailsRef}
                defaultChecked={showStoreDetails}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>

            {/* Show Customer Details */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="showCustomerDetails" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                {t('print_settings.show_customer_details')}
              </label>
              <input
                ref={showCustomerDetailsRef}
                defaultChecked={showCustomerDetails}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>

            {/* Print Token */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="printToken" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                {t('print_settings.print_token')}
              </label>
              <input
                ref={printTokenRef}
                defaultChecked={printToken}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>

            {/* Show Notes */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="showNotes" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                {t('print_settings.show_notes')}
              </label>
              <input
                ref={showNotesRef}
                defaultChecked={showNotes}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>

            {/* Auto Print KDS */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/50">
              <label htmlFor="kdsAutoPrint" className="font-medium text-sm text-gray-800 dark:text-zinc-200">
                Auto-Print Kitchen Orders (KDS)
              </label>
              <input
                checked={kdsAutoPrint}
                onChange={(e) => setKdsAutoPrint(e.target.checked)}
                type="checkbox"
                className="toggle toggle-success toggle-sm"
              />
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SECTION 3: HEADER & FOOTER CUSTOMIZATION */}
        {/* ========================================================================= */}
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-3xl p-6 shadow-sm space-y-5">
          <h4 className="text-xl font-bold text-gray-900 dark:text-white border-b pb-4">
            Receipt Header & Footer Notes
          </h4>

          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-zinc-300">
              Custom Header Note (Optional)
            </label>
            <textarea
              ref={headerRef}
              defaultValue={header}
              placeholder="e.g. Welcome to Hotel RestroPro • Authentic Dining & Hospitality"
              className="textarea textarea-bordered w-full h-20 rounded-2xl dark:bg-zinc-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-zinc-300">
              Custom Footer Note (Optional)
            </label>
            <textarea
              ref={footerRef}
              defaultValue={footer}
              placeholder="e.g. Thank you for your patronage! Wi-Fi: GuestHotel / Pass: welcome123"
              className="textarea textarea-bordered w-full h-20 rounded-2xl dark:bg-zinc-800 dark:text-white"
            />
          </div>

          <div className="pt-4">
            <button
              onClick={btnSave}
              className="btn bg-restro-green hover:bg-emerald-700 text-white w-full md:w-auto px-8 rounded-xl shadow-md gap-2"
            >
              <IconCheck size={18} /> {t('print_settings.save')}
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* LIVE PREVIEW MODAL */}
      {/* ========================================================================= */}
      {previewOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl rounded-3xl p-0 overflow-hidden bg-white shadow-lg">
            <div className="bg-emerald-700 p-5 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <IconEye size={20} /> Live Bill Preview
                </h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  Format: <b>{BILL_FORMATS.find(f => f.id === previewFormat)?.name || "Default"}</b> ({previewType === 'food' ? 'Restaurant Food Bill' : 'Hotel Room Bill'})
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewType(previewType === 'food' ? 'room' : 'food')}
                  className="btn btn-xs bg-white text-emerald-800 hover:bg-emerald-50 border-none rounded-lg"
                >
                  Switch to {previewType === 'food' ? 'Room Bill' : 'Food Bill'}
                </button>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="btn btn-sm btn-circle btn-ghost text-white"
                >
                  <IconX size={18} />
                </button>
              </div>
            </div>

            <div className="p-6 bg-gray-100 max-h-[75vh] overflow-y-auto flex justify-center">
              <div className="bg-white shadow-lg rounded-xl overflow-hidden p-2">
                {previewType === 'room' ? (
                  <iframe
                    title="Room Bill Preview"
                    sandbox=""
                    style={{ width: ((previewFormat || '').includes('a4') || (previewFormat || '').includes('folio')) ? '580px' : '360px', height: '620px', border: 'none', willChange: 'auto' }}
                    srcDoc={generateRoomBillHTML(mockFolioData, previewFormat, data, storeSettings, false, false)}
                  />
                ) : previewFormat === 'retail_tax_invoice' ? (
                <div style={{
                  width: '320px',
                  background: '#fff',
                  padding: '16px 10px',
                  fontFamily: "'Consolas', 'Courier New', Courier, Monaco, 'Lucida Console', monospace",
                  fontSize: '11px',
                  lineHeight: '1.35',
                  color: '#000',
                  boxShadow: '0 0 10px rgba(0,0,0,0.05)'
                }}>
                  {/* Header */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 900, lineHeight: 1.15 }}>
                      {storeSettings?.store_name || "demo"}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 500, marginTop: '1px' }}>
                      {storeSettings?.address || "demo"}
                    </div>
                    {(storeSettings?.phone || "987654") && (
                      <div style={{ fontSize: '12px', fontWeight: 500 }}>
                        {storeSettings?.phone || "987654"}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: 700, marginTop: '4px', textTransform: 'uppercase' }}>
                      Tax Invoice
                    </div>
                  </div>

                  {/* Metadata */}
                  <div style={{ marginTop: '8px', fontSize: '11px', lineHeight: 1.35 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{storeSettings?.pan_no || "617458148"}:</span>
                      <span>123456734</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Bill No :</span>
                      <span>d4515-2083/2084-T</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Area :</span>
                      <span>HALL</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Table No :</span>
                      <span>6</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Table Name :</span>
                      <span>TH-6</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Transaction Date :</span>
                      <span>{formatReceiptDateTime(new Date())}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Invoice Issue Date :</span>
                      <span>{formatReceiptDateTime(new Date())}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Nepali Date :</span>
                      <span>{getNepaliDateBS(new Date())}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ width: '130px' }}>Fiscal Year :</span>
                      <span>2083/2084</span>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #000', margin: '6px 0' }}></div>

                  {/* Items Table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #000' }}>
                        <th style={{ textAlign: 'left', padding: '3px 0', width: '42%' }}>Item</th>
                        <th style={{ textAlign: 'center', padding: '3px 0', width: '14%' }}>HSCode</th>
                        <th style={{ textAlign: 'right', padding: '3px 0', width: '14%' }}>QTY</th>
                        <th style={{ textAlign: 'right', padding: '3px 0', width: '15%' }}>Price</th>
                        <th style={{ textAlign: 'right', padding: '3px 0', width: '15%' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'left', padding: '2px 0' }}>Chicken Mo:Mo</td>
                        <td style={{ textAlign: 'center', padding: '2px 0' }}>0</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>2.00</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>250</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>500</td>
                      </tr>
                      <tr>
                        <td style={{ textAlign: 'left', padding: '2px 0' }}>Cold Drinks</td>
                        <td style={{ textAlign: 'center', padding: '2px 0' }}>0</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>1.00</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>65</td>
                        <td style={{ textAlign: 'right', padding: '2px 0' }}>65</td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ borderTop: '1px solid #000', margin: '6px 0' }}></div>

                  {/* Payment Box & Totals */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '4px 0' }}>
                    {/* Left: Mode of Payment Box */}
                    <div style={{ border: '1px solid #000', padding: '4px 6px', width: '43%', minHeight: '50px', fontSize: '11px' }}>
                      <div style={{ fontWeight: 700, borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '2px' }}>Mode Of Payment</div>
                      <div style={{ fontWeight: 600 }}>FonePay</div>
                      <div style={{ fontWeight: 600 }}>565</div>
                    </div>

                    {/* Right: Totals Breakdown */}
                    <div style={{ width: '54%', fontSize: '11px', lineHeight: '1.45' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Sub-Total :</span>
                        <span>565</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>NonTaxable AMT</span>
                        <span>565</span>
                      </div>
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '3px 0', margin: '6px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '16px', fontWeight: 900 }}>Grand Total:</span>
                    <span style={{ fontSize: '16px', fontWeight: 900 }}>565</span>
                  </div>

                  {/* In Words & Received */}
                  <div style={{ fontSize: '10.5px', marginTop: '4px', lineHeight: '1.35' }}>
                    <div>In Words: Five Hundred Sixty Five Only</div>
                    <div style={{ textAlign: 'right', marginTop: '5px', fontWeight: 600 }}>
                      Received AMT : 0.00
                    </div>
                  </div>

                  {/* Thank You */}
                  <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 700, margin: '12px 0 14px 0' }}>
                    Thank You.
                  </div>

                  {/* Signatures */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', marginTop: '12px', padding: '0 4px' }}>
                    <div>
                      <div style={{ width: '80px', borderTop: '1px solid #000', marginBottom: '2px' }}></div>
                      <div>Cashier</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ width: '80px', borderTop: '1px solid #000', marginBottom: '2px', marginLeft: 'auto' }}></div>
                      <div>Customer</div>
                    </div>
                  </div>

                  {/* Footer Branding */}
                  <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '14px', color: '#222', lineHeight: '1.3' }}>
                    {footer ? (
                      <div>{footer}</div>
                    ) : (
                      <>
                        <div>Powered By : STJ Technologies</div>
                        <div>www.ehospitalitynepal.com</div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  width: ((previewFormat || '').includes('a4') || (previewFormat || '').includes('folio')) ? '580px' : '360px',
                  background: '#fff',
                  padding: '16px',
                  fontFamily: previewFormat === 'thermal_ird' ? "'Courier New', Courier, monospace" : (previewFormat === 'luxury_folio' ? "'Garamond', Georgia, serif" : "-apple-system, sans-serif"),
                  fontSize: '11px',
                  lineHeight: 1.4
                }}>
                  {/* Sample Food Bill Mockup */}
                  <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 900, textTransform: 'uppercase' }}>
                      {storeSettings?.store_name || "RestroPro Dining"}
                    </div>
                    <div style={{ fontSize: '10.5px' }}>{storeSettings?.address || "Lakeside-6, Pokhara, Nepal"}</div>
                    <div style={{ fontSize: '10.5px' }}>Tel: {storeSettings?.phone || "+977 61-460000"}</div>
                    <div style={{ fontWeight: 'bold' }}>PAN No: {storeSettings?.pan_no || "601234567"}</div>
                    <div style={{ marginTop: '4px', fontWeight: 900, fontSize: '13px' }}>
                      TAX INVOICE / कर बिजक
                    </div>
                  </div>

                  <div style={{ padding: '6px 0', fontSize: '10.5px' }}>
                    <div><b>Invoice No:</b> INV-5829</div>
                    <div><b>Fiscal Year:</b> 2081/082</div>
                    <div><b>Date:</b> {new Date().toLocaleString()}</div>
                    <div><b>Type:</b> Dine-In (Table: T-04)</div>
                    <div><b>Buyer:</b> Walking Guest (PAN: N/A)</div>
                  </div>

                  <table style={{ width: '100%', borderTop: '1px dashed #000', borderBottom: '1px dashed #000', margin: '6px 0', fontSize: '10.5px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px dashed #000' }}>
                        <th style={{ textAlign: 'left', padding: '4px 0' }}>Item</th>
                        <th style={{ textAlign: 'center', width: '15%' }}>Qty</th>
                        <th style={{ textAlign: 'right', width: '25%' }}>Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 0' }}>Chicken Mo:Mo</td>
                        <td style={{ textAlign: 'center' }}>2</td>
                        <td style={{ textAlign: 'right' }}>Rs. 500.00</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 0' }}>Mint Lemonade</td>
                        <td style={{ textAlign: 'center' }}>2</td>
                        <td style={{ textAlign: 'right' }}>Rs. 300.00</td>
                      </tr>
                    </tbody>
                  </table>

                  <table style={{ width: '100%', fontSize: '11px', marginTop: '4px' }}>
                    <tbody>
                      <tr><td>Subtotal:</td><td style={{ textAlign: 'right' }}>Rs. 800.00</td></tr>
                      <tr><td>Taxable Amount:</td><td style={{ textAlign: 'right' }}>Rs. 800.00</td></tr>
                      <tr><td>13% VAT:</td><td style={{ textAlign: 'right' }}>Rs. 104.00</td></tr>
                      <tr style={{ fontWeight: 900, fontSize: '13px', borderTop: '1px solid #000' }}>
                        <td>Grand Total:</td><td style={{ textAlign: 'right' }}>Rs. 904.00</td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ fontSize: '9.5px', marginTop: '6px', background: '#f9f9f9', padding: '4px', borderRadius: '4px' }}>
                    <b>Amount in Words:</b> <i>Nine Hundred Four Rupees Only</i>
                  </div>

                  <div style={{ textAlign: 'center', fontSize: '9px', color: '#666', marginTop: '10px' }}>
                    <div>माल सामान तथा सेवा फिर्ता हुँदैन।</div>
                    <div>THIS IS A COMPUTER GENERATED TAX INVOICE</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
            <span className="text-xs text-gray-500">
              This preview matches your store credentials and active IRD template standards.
            </span>
            <button
              onClick={() => {
                btnQuickSaveFormat(previewFormat);
                setPreviewOpen(false);
              }}
              className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            >
              <IconCheck size={16} /> Use This Format
            </button>
          </div>
        </div>
      </dialog>
      )}
    </Page>
  );
}
